import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Volume2, VolumeX, Settings } from 'lucide-react';
import voiceService from '../utils/voiceService';
import { toast } from 'sonner';

const VoiceSettings = () => {
  const [voiceEnabled, setVoiceEnabled] = useState(voiceService.isEnabled);
  const [isTestPlaying, setIsTestPlaying] = useState(false);

  const toggleVoice = () => {
    const newValue = !voiceEnabled;
    setVoiceEnabled(newValue);
    voiceService.setVoicePreference(newValue);
    
    if (newValue) {
      toast.success('Voice greeting enabled');
    } else {
      toast.info('Voice greeting disabled');
      voiceService.stop(); // Stop any ongoing speech
    }
  };

  const testVoiceGreeting = () => {
    if (!voiceService.isSupported()) {
      toast.error('Speech synthesis not supported in your browser');
      return;
    }

    if (!voiceEnabled) {
      toast.error('Please enable voice greeting first');
      return;
    }

    setIsTestPlaying(true);
    
    // Get user name for personalized test
    const userStr = localStorage.getItem('fokatmain_user');
    let userName = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userName = user.username || user.email?.split('@')[0];
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }

    voiceService.speak(
      voiceService.getGreeting(userName),
      () => {
        setIsTestPlaying(false);
        toast.success('Test complete!');
      }
    );
  };

  const resetDailyGreeting = () => {
    localStorage.removeItem('voice_greeting_last_played');
    localStorage.removeItem('welcome_splash_shown');
    toast.success('Daily greeting reset! You will hear the welcome message on next app launch.');
  };

  return (
    <Card className="gaming-card">
      <CardHeader>
        <CardTitle className="text-yellow-400 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Voice Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Voice Greeting Toggle */}
        <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-gray-800">
          <div className="flex items-center gap-3">
            {voiceEnabled ? (
              <Volume2 className="w-6 h-6 text-yellow-400" />
            ) : (
              <VolumeX className="w-6 h-6 text-gray-500" />
            )}
            <div>
              <p className="text-white font-semibold">Voice Welcome Greeting</p>
              <p className="text-gray-400 text-sm">
                {voiceEnabled ? 'Enabled - Hear personalized welcome' : 'Disabled - Silent mode'}
              </p>
            </div>
          </div>
          <Button
            onClick={toggleVoice}
            className={`${
              voiceEnabled 
                ? 'bg-yellow-400 hover:bg-yellow-500 text-black' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            } font-semibold px-6 py-2 rounded-lg transition-all`}
          >
            {voiceEnabled ? 'ON' : 'OFF'}
          </Button>
        </div>

        {/* Info Text */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <p className="text-gray-300 text-sm mb-2">
            📱 <span className="font-semibold">About Voice Greeting:</span>
          </p>
          <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">
            <li>Plays once per day when you open the PWA app</li>
            <li>Personalized with your name when logged in</li>
            <li>Uses Hinglish (Indian English accent)</li>
            <li>Requires user interaction to play (tap to start)</li>
          </ul>
        </div>

        {/* Test Button */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={testVoiceGreeting}
            disabled={isTestPlaying || !voiceEnabled}
            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isTestPlaying ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Playing Test...
              </span>
            ) : (
              '🔊 Test Voice Greeting'
            )}
          </Button>

          <Button
            onClick={resetDailyGreeting}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-all"
          >
            🔄 Reset Daily Greeting
          </Button>
        </div>

        {/* Browser Support Info */}
        {!voiceService.isSupported() && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 text-sm">
              ⚠️ Speech synthesis is not supported in your browser. Voice greeting will not work.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VoiceSettings;
